import { createColumnHelper } from '@atbay/cyber-components'
import { FC, useMemo } from 'react'
import CaseNumberDisplay from '@/ui/components/caseNumberDisplay'
import { HeaderStatusBadge } from '@/ui/components/HeaderStatusBadge'
import { TAlert, TIncident } from '@core/modules/mdr/cases'
import { formatDate } from '@core/utils/time'
import { StyledCustomTable } from '../CustomTable/CustomTable.styles'
import SeverityIcon from '../svg/SeverityIcon'
import TableEmpty from '../table/table-empty'
import Typography from '../typography'
import { Cell, HeaderText, SubjectCell } from './CasesTable.styles'
import {
    compareSeverity,
    TStatus,
    columnSortingConfig,
    NOT_AVAILABLE,
} from './CasesTable.utils'
import { useCasesTable, TCurrentTab } from './useCasesTable'

interface CasesTableProps {
    cases: TIncident[] | TAlert[]
    currentTab: TCurrentTab
}

const CasesTable: FC<CasesTableProps> = ({ cases, currentTab }) => {
    const {
        sorting,
        setSorting,
        emptyStateText,
        activeCaseNumber,
        openDrawer,
        tableTitle,
    } = useCasesTable({
        cases,
        currentTab,
    })

    const columnHelper = createColumnHelper<TAlert | TIncident>()

    const baseColumns = useMemo(
        () => [
            {
                ...columnHelper.accessor('severity', {
                    cell: (info) => (
                        <Cell
                            onClick={() => openDrawer(info.row, info.table)}
                            data-testid={`mdr-cases-table-severity-${info.cell.getValue().toLowerCase()}`}
                        >
                            <SeverityIcon
                                severity={
                                    info.cell
                                        .getValue()
                                        .toLowerCase() as TIssueSeverity
                                }
                            />
                            <Typography className="table-cell-text capitalize severity-text">
                                {info.cell.getValue() as TCaseSeverity}
                            </Typography>
                        </Cell>
                    ),
                    header: () => (
                        <HeaderText
                            variant="xs-regular"
                            className="table-cell-text"
                        >
                            Severity
                        </HeaderText>
                    ),
                    enableSorting: columnSortingConfig['severity'],
                    sortingFn: (a, b) => {
                        return compareSeverity(
                            a.original.severity,
                            b.original.severity
                        )
                    },
                }),
            },
            {
                id: 'caseNumber',
                header: () => (
                    <HeaderText
                        variant="xs-regular"
                        className="table-cell-text"
                    >
                        Case Number
                    </HeaderText>
                ),
                cell: (info: any) => {
                    const caseNumber = info.row.original.caseNumber as string
                    return (
                        <Cell
                            cursor="text"
                            data-testid="mdr-cases-table-case-number"
                        >
                            <CaseNumberDisplay caseNumber={caseNumber} />
                        </Cell>
                    )
                },
            },
            {
                ...columnHelper.accessor('title', {
                    cell: (info) => (
                        <SubjectCell
                            data-testid="mdr-cases-table-subject"
                            onClick={() => openDrawer(info.row, info.table)}
                        >
                            <Typography className="table-cell-text">
                                {info.cell.getValue() ?? ''}
                            </Typography>
                        </SubjectCell>
                    ),
                    header: () => (
                        <HeaderText
                            variant="xs-regular"
                            className="table-cell-text"
                        >
                            Subject
                        </HeaderText>
                    ),
                    enableSorting: columnSortingConfig['title'],
                }),
                width: '26%',
            },
            {
                ...columnHelper.accessor('status', {
                    cell: (info) => (
                        <Cell
                            data-testid="mdr-cases-table-status"
                            onClick={() => {
                                openDrawer(info.row, info.table)
                            }}
                        >
                            <HeaderStatusBadge
                                status={info.cell.getValue() as TStatus}
                                variant="incident"
                                className="table-cell-text"
                            />
                        </Cell>
                    ),
                    header: () => (
                        <HeaderText
                            variant="xs-regular"
                            className="table-cell-text"
                        >
                            Status
                        </HeaderText>
                    ),
                    enableSorting: columnSortingConfig['status'],
                }),
            },
            {
                ...columnHelper.accessor('createdAt', {
                    cell: (info) => (
                        <Cell
                            data-testid="mdr-cases-table-created-date"
                            onClick={() => openDrawer(info.row, info.table)}
                        >
                            <Typography className="table-cell-text">
                                {formatDate(info.cell.getValue())}
                            </Typography>
                        </Cell>
                    ),
                    header: () => (
                        <HeaderText
                            variant="xs-regular"
                            className="table-cell-text"
                        >
                            Created Date
                        </HeaderText>
                    ),
                    enableSorting: columnSortingConfig['createdAt'],
                }),
            },
            {
                ...columnHelper.accessor('disposition', {
                    cell: (info) => (
                        <Cell
                            data-testid="mdr-cases-table-disposition"
                            onClick={() => openDrawer(info.row, info.table)}
                        >
                            <Typography className="table-cell-text">
                                {info.cell.getValue() ?? ''}
                            </Typography>
                        </Cell>
                    ),
                    header: () => (
                        <HeaderText
                            variant="xs-regular"
                            className="table-cell-text"
                        >
                            Disposition
                        </HeaderText>
                    ),
                    enableSorting: columnSortingConfig['disposition'],
                }),
            },
        ],
        [columnHelper, openDrawer]
    )

    const columns = useMemo(() => {
        const result = [...baseColumns]

        if (currentTab === 'alerts') {
            const mdrServiceColumn = {
                ...columnHelper.accessor('caseSource', {
                    cell: (info) => (
                        <Cell
                            padding={
                                columnSortingConfig['caseSource']
                                    ? undefined
                                    : '0 12px 0 0'
                            }
                            data-testid="mdr-cases-table-mdr-service"
                            onClick={() => openDrawer(info.row, info.table)}
                        >
                            <Typography className="table-cell-text">
                                {(info.cell.getValue() as string) ??
                                    NOT_AVAILABLE}
                            </Typography>
                        </Cell>
                    ),
                    header: () => (
                        <HeaderText
                            variant="xs-regular"
                            className="table-cell-text"
                        >
                            MDR Service
                        </HeaderText>
                    ),
                    enableSorting: columnSortingConfig['caseSource'],
                }),
            }
            result.splice(3, 0, mdrServiceColumn)
        }

        return result
    }, [baseColumns, currentTab, columnHelper, openDrawer])

    return (
        <div data-testid={`mdr-page-${currentTab}-table`}>
            <StyledCustomTable
                activeRow={activeCaseNumber}
                getRowId={(row) => (row as TAlert | TIncident).caseNumber}
                tableHeight={700}
                columns={columns}
                data={cases}
                sorting={sorting}
                setSorting={setSorting}
                emptyState={<TableEmpty message={emptyStateText} />}
            />
        </div>
    )
}

export default CasesTable
